/**
 * Performance Monitor
 * 
 * Singleton class for tracking and measuring performance across the application.
 * Integrates with Google Analytics 4 for telemetry and provides local console logging in development.
 */

import { evaluateMetric, MetricCategory, getMetric } from './performanceMetrics';
import { RECORDING_FLAGS } from '../constants/recordingFlags';

export interface PerformanceMeasurement {
  name: string;
  value: number;
  rating: 'good' | 'acceptable' | 'poor' | 'unknown';
  timestamp: number;
  category?: MetricCategory;
}

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private measurements: Map<string, PerformanceMeasurement> = new Map();
  private isEnabled: boolean = true;

  private constructor() {
    // Initialize Core Web Vitals tracking
    this.initCoreWebVitals();
  }

  static getInstance(): PerformanceMonitor {
    if (!this.instance) {
      this.instance = new PerformanceMonitor();
    }
    return this.instance;
  }

  /**
   * Enable or disable performance monitoring
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Mark the start of a performance measurement
   */
  mark(name: string): void {
    if (!this.isEnabled || typeof performance === 'undefined') return;

    try {
      performance.mark(`${name}-start`);
    } catch (error) {
      console.warn(`Failed to mark ${name}:`, error);
    }
  }

  /**
   * Mark the end and measure duration
   */
  measure(name: string): number | null {
    if (!this.isEnabled || typeof performance === 'undefined') return null;

    try {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);

      const measureEntries = performance.getEntriesByName(name, 'measure');
      const duration = measureEntries.length > 0 ? measureEntries[0].duration : 0;

      // Evaluate metric against threshold
      const rating = evaluateMetric(name, duration);
      const metric = getMetric(name);

      const measurement: PerformanceMeasurement = {
        name,
        value: duration,
        rating,
        timestamp: Date.now(),
        category: metric?.category,
      };

      this.measurements.set(name, measurement);
      this.logMetric(measurement);

      // Clean up marks
      performance.clearMarks(`${name}-start`);
      performance.clearMarks(`${name}-end`);
      performance.clearMeasures(name);

      return duration;
    } catch (error) {
      console.warn(`Performance measurement failed for ${name}:`, error);
      return null;
    }
  }

  /**
   * Record a custom metric value directly
   */
  recordMetric(name: string, value: number, category?: MetricCategory): void {
    if (!this.isEnabled) return;

    const rating = evaluateMetric(name, value);
    const measurement: PerformanceMeasurement = {
      name,
      value,
      rating,
      timestamp: Date.now(),
      category,
    };

    this.measurements.set(name, measurement);
    this.logMetric(measurement);
  }

  /**
   * Log metric to analytics and console
   */
  private logMetric(measurement: PerformanceMeasurement): void {
    // Log to Google Analytics 4
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'performance_metric', {
        metric_name: measurement.name,
        metric_value: Math.round(measurement.value),
        metric_rating: measurement.rating,
        metric_category: measurement.category || 'unknown',
      });
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      const emoji = measurement.rating === 'good' ? '✅' : measurement.rating === 'acceptable' ? '⚠️' : '❌';
      console.log(
        `${emoji} [Performance] ${measurement.name}: ${measurement.value.toFixed(2)}ms (${measurement.rating})`
      );
    }
  }

  /**
   * Measure FPS during a specific duration
   */
  measureFPS(duration: number = 1000): Promise<number> {
    if (!this.isEnabled) return Promise.resolve(0);

    return new Promise((resolve) => {
      let frameCount = 0;
      const startTime = performance.now();

      const countFrame = () => {
        frameCount++;
        const elapsed = performance.now() - startTime;

        if (elapsed < duration) {
          requestAnimationFrame(countFrame);
        } else {
          const fps = Math.round((frameCount / elapsed) * 1000);
          this.recordMetric('CANVAS_FPS', fps, MetricCategory.CANVAS);
          resolve(fps);
        }
      };

      requestAnimationFrame(countFrame);
    });
  }

  /**
   * Get memory usage (if available)
   */
  getMemoryUsage(): number | null {
    if (typeof performance === 'undefined' || !(performance as any).memory) {
      return null;
    }

    const memory = (performance as any).memory;
    const usedMB = memory.usedJSHeapSize / (1024 * 1024);
    return Math.round(usedMB);
  }

  /**
   * Record memory usage
   */
  recordMemoryUsage(): void {
    const memoryMB = this.getMemoryUsage();
    if (memoryMB !== null) {
      this.recordMetric('MEMORY_USAGE', memoryMB);
    }
  }

  /**
   * Get all measurements
   */
  getAllMeasurements(): PerformanceMeasurement[] {
    return Array.from(this.measurements.values());
  }

  /**
   * Get measurements by category
   */
  getMeasurementsByCategory(category: MetricCategory): PerformanceMeasurement[] {
    return this.getAllMeasurements().filter(m => m.category === category);
  }

  /**
   * Clear all measurements
   */
  clearMeasurements(): void {
    this.measurements.clear();
  }

  /**
   * Initialize Core Web Vitals tracking
   */
  private initCoreWebVitals(): void {
    if (typeof window === 'undefined') return;

    // Track LCP (Largest Contentful Paint)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as any;
      this.recordMetric('LCP', lastEntry.renderTime || lastEntry.loadTime, MetricCategory.CORE_WEB_VITALS);
    });

    try {
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      // LCP not supported
    }

    // Track FCP (First Contentful Paint)
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.name === 'first-contentful-paint') {
          this.recordMetric('FCP', entry.startTime, MetricCategory.CORE_WEB_VITALS);
        }
      });
    });

    try {
      fcpObserver.observe({ entryTypes: ['paint'] });
    } catch (e) {
      // FCP not supported
    }

    // Track CLS (Cumulative Layout Shift)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
          this.recordMetric('CLS', clsValue, MetricCategory.CORE_WEB_VITALS);
        }
      }
    });

    try {
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      // CLS not supported
    }

    // Track FID (First Input Delay)
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        this.recordMetric('FID', entry.processingStart - entry.startTime, MetricCategory.CORE_WEB_VITALS);
      });
    });

    try {
      fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      // FID not supported
    }
  }

  /**
   * Export performance report
   */
  generateReport(): {
    summary: {
      total: number;
      good: number;
      acceptable: number;
      poor: number;
    };
    measurements: PerformanceMeasurement[];
    byCategory: Record<string, PerformanceMeasurement[]>;
  } {
    const measurements = this.getAllMeasurements();
    const summary = {
      total: measurements.length,
      good: measurements.filter(m => m.rating === 'good').length,
      acceptable: measurements.filter(m => m.rating === 'acceptable').length,
      poor: measurements.filter(m => m.rating === 'poor').length,
    };

    const byCategory: Record<string, PerformanceMeasurement[]> = {};
    measurements.forEach(m => {
      const category = m.category || 'unknown';
      if (!byCategory[category]) {
        byCategory[category] = [];
      }
      byCategory[category].push(m);
    });

    return { summary, measurements, byCategory };
  }
}

// Export singleton instance
export const performanceMonitor = PerformanceMonitor.getInstance();

// Export convenience functions
export const markPerformance = (name: string) => performanceMonitor.mark(name);
export const measurePerformance = (name: string) => performanceMonitor.measure(name);
export const recordMetric = (name: string, value: number, category?: MetricCategory) =>
  performanceMonitor.recordMetric(name, value, category);

/**
 * Frame Timing Telemetry (Phase 3)
 * 
 * Collects per-frame metrics for 30 FPS compliance verification.
 * All functionality gated behind RECORDING_FLAGS.TELEMETRY_ENABLED.
 * 
 * @see Base Docs/Canvas_Recording_Implementation_Plan.md - Task 3.1
 */

export interface FrameMetrics {
  frameNumber: number;
  targetTimestamp: number;
  actualTimestamp: number;
  renderDurationMs: number;
  isDuplicate: boolean;
  isHiddenTab: boolean;
  drift: number;
}

export interface RecordingMetrics {
  totalFrames: number;
  duplicatedFrames: number;
  avgRenderMs: number;
  p95RenderMs: number;
  maxRenderMs: number;
  avgDriftMs: number;
  maxDriftMs: number;
  actualFps: number;
  hiddenTabFrames: number;
}

export class FrameTimingCollector {
  private static instance: FrameTimingCollector;
  private frames: FrameMetrics[] = [];
  private maxFrames = 1800;
  private recordingStartTime: number = 0;
  
  private constructor() {}
  
  static getInstance(): FrameTimingCollector {
    if (!this.instance) {
      this.instance = new FrameTimingCollector();
    }
    return this.instance;
  }
  
  startRecording(): void {
    this.frames = [];
    this.recordingStartTime = performance.now();
  }
  
  recordFrame(metrics: FrameMetrics): void {
    if (!RECORDING_FLAGS.TELEMETRY_ENABLED) return;
    
    this.frames.push(metrics);
    if (this.frames.length > this.maxFrames) {
      this.frames.shift();
    }
    
    if (RECORDING_FLAGS.DRIFT_ALERTS && Math.abs(metrics.drift) > 5) {
      console.warn(`[FrameTiming] Frame ${metrics.frameNumber} drift exceeded: ${metrics.drift.toFixed(1)}ms`);
    }
  }
  
  getRecordingMetrics(): RecordingMetrics {
    if (this.frames.length === 0) {
      return {
        totalFrames: 0,
        duplicatedFrames: 0,
        avgRenderMs: 0,
        p95RenderMs: 0,
        maxRenderMs: 0,
        avgDriftMs: 0,
        maxDriftMs: 0,
        actualFps: 0,
        hiddenTabFrames: 0,
      };
    }
    
    const renders = this.frames.map(f => f.renderDurationMs).sort((a, b) => a - b);
    const drifts = this.frames.map(f => Math.abs(f.drift));
    
    return {
      totalFrames: this.frames.length,
      duplicatedFrames: this.frames.filter(f => f.isDuplicate).length,
      avgRenderMs: renders.reduce((a, b) => a + b, 0) / renders.length,
      p95RenderMs: renders[Math.floor(renders.length * 0.95)] || 0,
      maxRenderMs: Math.max(...renders),
      avgDriftMs: drifts.reduce((a, b) => a + b, 0) / drifts.length,
      maxDriftMs: Math.max(...drifts),
      actualFps: this.calculateActualFps(),
      hiddenTabFrames: this.frames.filter(f => f.isHiddenTab).length,
    };
  }
  
  private calculateActualFps(): number {
    if (this.frames.length < 2) return 0;
    const first = this.frames[0].actualTimestamp;
    const last = this.frames[this.frames.length - 1].actualTimestamp;
    const durationUs = last - first;
    if (durationUs <= 0) return 0;
    return (this.frames.length - 1) / (durationUs / 1_000_000);
  }
  
  getFrameCount(): number {
    return this.frames.length;
  }
  
  clear(): void {
    this.frames = [];
    this.recordingStartTime = 0;
  }
  
  logSummary(): void {
    if (!RECORDING_FLAGS.TELEMETRY_ENABLED) return;
    
    const metrics = this.getRecordingMetrics();
    console.log('[FrameTiming] Recording Summary:', {
      totalFrames: metrics.totalFrames,
      duplicatedFrames: metrics.duplicatedFrames,
      duplicateRate: ((metrics.duplicatedFrames / metrics.totalFrames) * 100).toFixed(2) + '%',
      avgRenderMs: metrics.avgRenderMs.toFixed(2) + 'ms',
      p95RenderMs: metrics.p95RenderMs.toFixed(2) + 'ms',
      maxRenderMs: metrics.maxRenderMs.toFixed(2) + 'ms',
      avgDriftMs: metrics.avgDriftMs.toFixed(2) + 'ms',
      maxDriftMs: metrics.maxDriftMs.toFixed(2) + 'ms',
      actualFps: metrics.actualFps.toFixed(2),
      hiddenTabFrames: metrics.hiddenTabFrames,
    });
  }
}

export const frameTimingCollector = FrameTimingCollector.getInstance();

type CloseableResource = { close: () => void };

export interface MemoryGuardResult<T> {
  success: boolean;
  result?: T;
  error?: Error;
}

export async function withMemoryGuard<T>(
  resources: CloseableResource[],
  operation: () => T | Promise<T>
): Promise<MemoryGuardResult<T>> {
  try {
    const result = await operation();
    return { success: true, result };
  } catch (error) {
    console.error('[MemoryGuard] Operation failed, cleaning up resources:', error);
    return { success: false, error: error instanceof Error ? error : new Error(String(error)) };
  } finally {
    for (const resource of resources) {
      try {
        resource.close();
      } catch (closeError) {
        console.warn('[MemoryGuard] Failed to close resource:', closeError);
      }
    }
  }
}

export function safeCloseResource(resource: CloseableResource | null | undefined, label?: string): void {
  if (!resource) return;
  try {
    // Call close() and handle both sync and async variants
    // WritableStreamDefaultWriter.close() returns a Promise
    const result = resource.close() as unknown;
    // Handle async close() methods - catch rejections to prevent unhandled promise rejections
    if (result && typeof result === 'object' && 'catch' in result && typeof (result as any).catch === 'function') {
      (result as Promise<void>).catch(() => {
        // Silently swallow - shutdown errors are expected
      });
    }
  } catch (error) {
    // Synchronous errors are also swallowed during cleanup
  }
}
